/**
 * IP Geolocation Service
 * Responsible for fetching user's IP address and geolocation data from external APIs
 */

export class IPGeolocationService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('IPGeolocationService');
        this.cache = null;
        this.cacheTimeout = 300000; // 5 minutes
        this.cacheTimestamp = 0;

        // Use multiple providers for redundancy
        this.providers = [
            {
                name: 'ipapi',
                url: 'https://ipapi.co/json/',
                transform: (data) => ({
                    ip: data.ip,
                    country: data.country_code,
                    countryName: data.country_name,
                    region: data.region,
                    city: data.city,
                    timezone: data.timezone,
                    isp: data.org,
                    asn: data.asn,
                    isVPN: false, // ipapi.co doesn't provide this in free tier
                    isProxy: false,
                    isTor: false
                })
            },
            {
                name: 'ipinfo',
                url: 'https://ipinfo.io/json',
                transform: (data) => ({
                    ip: data.ip,
                    country: data.country,
                    countryName: data.country,
                    region: data.region,
                    city: data.city,
                    timezone: data.timezone,
                    isp: data.org,
                    asn: data.org?.split(' ')[0] || null,
                    isVPN: false,
                    isProxy: false,
                    isTor: false
                })
            },
            {
                name: 'ip-api',
                url: 'http://ip-api.com/json/?fields=status,message,country,countryCode,region,city,zip,lat,lon,timezone,isp,org,as,proxy,hosting,query',
                transform: (data) => ({
                    ip: data.query,
                    country: data.countryCode,
                    countryName: data.country,
                    region: data.region,
                    city: data.city,
                    timezone: data.timezone,
                    isp: data.isp,
                    asn: data.as,
                    isVPN: data.hosting || false, // hosting often indicates VPN/datacenter
                    isProxy: data.proxy || false,
                    isTor: false,
                    lat: data.lat,
                    lon: data.lon
                })
            }
        ];

        this.stats = {
            requests: 0,
            successes: 0,
            failures: 0,
            cacheHits: 0
        };
    }

    /**
     * Get geolocation data for the current user's IP
     */
    async getGeolocation() {
        this.stats.requests++;

        // Check cache first
        if (this.cache && (Date.now() - this.cacheTimestamp) < this.cacheTimeout) {
            this.stats.cacheHits++;
            this.logger.debug('Returning cached geolocation data');
            return this.cache;
        }

        // Try each provider in order
        for (const provider of this.providers) {
            try {
                const data = await this.fetchFromProvider(provider);
                if (data) {
                    this.cache = data;
                    this.cacheTimestamp = Date.now();
                    this.stats.successes++;
                    this.logger.info('Geolocation data fetched', { provider: provider.name, country: data.country });
                    return data;
                }
            } catch (error) {
                this.logger.warn(`Provider ${provider.name} failed`, { error: error.message });
            }
        }

        this.stats.failures++;
        this.logger.error('All geolocation providers failed');
        return null;
    }

    /**
     * Fetch data from a specific provider
     */
    async fetchFromProvider(provider) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
            const response = await fetch(provider.url, {
                method: 'GET',
                signal: controller.signal,
                headers: {
                    'Accept': 'application/json'
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            return provider.transform(data);

        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    /**
     * Compare browser timezone with IP geolocation
     */
    async detectTimezoneIPMismatch() {
        const geolocation = await this.getGeolocation();
        if (!geolocation) {
            return { detected: false, confidence: 0, reason: 'Unable to get geolocation' };
        }

        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const ipTimezone = geolocation.timezone;

        if (!ipTimezone) {
            return { detected: false, confidence: 0, reason: 'No timezone from IP' };
        }

        // Extract region from timezone (e.g., "America/New_York" -> "America")
        const browserRegion = browserTimezone.split('/')[0];
        const ipRegion = ipTimezone.split('/')[0];

        if (browserRegion !== ipRegion) {
            return {
                detected: true,
                confidence: 80,
                reason: `Timezone mismatch: browser=${browserTimezone}, IP=${ipTimezone}`,
                browserTimezone,
                ipTimezone,
                ipCountry: geolocation.country
            };
        }

        // Check city-level mismatch (lower confidence)
        if (browserTimezone !== ipTimezone) {
            return {
                detected: true,
                confidence: 40,
                reason: `City-level timezone difference: browser=${browserTimezone}, IP=${ipTimezone}`,
                browserTimezone,
                ipTimezone,
                ipCountry: geolocation.country
            };
        }

        return { detected: false, confidence: 0, browserTimezone, ipTimezone };
    }

    /**
     * Compare browser language with IP country
     */
    async detectLanguageCountryMismatch() {
        const geolocation = await this.getGeolocation();
        if (!geolocation) {
            return { detected: false, confidence: 0, reason: 'Unable to get geolocation' };
        }

        const browserLanguage = navigator.language || navigator.userLanguage;
        const ipCountry = geolocation.country;

        // Map country codes to expected primary languages
        const countryLanguageMap = {
            'US': ['en'],
            'GB': ['en'],
            'CA': ['en', 'fr'],
            'AU': ['en'],
            'DE': ['de'],
            'FR': ['fr'],
            'ES': ['es'],
            'IT': ['it'],
            'PT': ['pt'],
            'BR': ['pt'],
            'JP': ['ja'],
            'CN': ['zh'],
            'KR': ['ko'],
            'RU': ['ru'],
            'MX': ['es'],
            'AR': ['es'],
            'NL': ['nl'],
            'BE': ['nl', 'fr', 'de'],
            'CH': ['de', 'fr', 'it'],
            'AT': ['de'],
            'PL': ['pl'],
            'SE': ['sv'],
            'NO': ['no', 'nb'],
            'DK': ['da'],
            'FI': ['fi'],
            'IN': ['en', 'hi'],
            'ID': ['id'],
            'TH': ['th'],
            'VN': ['vi'],
            'PH': ['en', 'tl'],
            'MY': ['ms', 'en'],
            'SG': ['en', 'zh', 'ms', 'ta'],
            'TW': ['zh'],
            'HK': ['zh', 'en'],
            'IL': ['he'],
            'SA': ['ar'],
            'AE': ['ar', 'en'],
            'EG': ['ar'],
            'TR': ['tr'],
            'ZA': ['en', 'af'],
            'NG': ['en'],
            'KE': ['en', 'sw']
        };

        const expectedLanguages = countryLanguageMap[ipCountry];
        if (!expectedLanguages) {
            // Unknown country, can't determine mismatch
            return { detected: false, confidence: 0, reason: 'Unknown country language mapping' };
        }

        const browserLangCode = browserLanguage.split('-')[0].toLowerCase();
        const matches = expectedLanguages.some(lang => browserLangCode === lang);

        if (!matches) {
            return {
                detected: true,
                confidence: 60,
                reason: `Language/Country mismatch: browser=${browserLanguage}, IP country=${ipCountry}`,
                browserLanguage,
                ipCountry,
                expectedLanguages
            };
        }

        return { detected: false, confidence: 0, browserLanguage, ipCountry };
    }

    /**
     * Check if IP is from known VPN/Proxy/Datacenter
     */
    async detectVPNIP() {
        const geolocation = await this.getGeolocation();
        if (!geolocation) {
            return { detected: false, confidence: 0, reason: 'Unable to get geolocation' };
        }

        const indicators = [];

        // Check VPN/Proxy/Hosting flags from API
        if (geolocation.isVPN) {
            indicators.push('IP flagged as VPN');
        }
        if (geolocation.isProxy) {
            indicators.push('IP flagged as Proxy');
        }
        if (geolocation.isTor) {
            indicators.push('IP flagged as Tor exit node');
        }

        // Check for datacenter/hosting ASN patterns
        const datacenterKeywords = ['hosting', 'cloud', 'server', 'data center', 'datacenter', 'vps', 'digital ocean', 'amazon', 'aws', 'google cloud', 'azure', 'linode', 'vultr', 'ovh'];
        const asnLower = (geolocation.asn || '').toLowerCase();
        const ispLower = (geolocation.isp || '').toLowerCase();

        for (const keyword of datacenterKeywords) {
            if (asnLower.includes(keyword) || ispLower.includes(keyword)) {
                indicators.push(`Datacenter ISP detected: ${geolocation.isp}`);
                break;
            }
        }

        if (indicators.length > 0) {
            return {
                detected: true,
                confidence: Math.min(indicators.length * 40, 95),
                indicators,
                ip: geolocation.ip,
                isp: geolocation.isp,
                asn: geolocation.asn
            };
        }

        return { detected: false, confidence: 0, ip: geolocation.ip, isp: geolocation.isp };
    }

    /**
     * Get service statistics
     */
    getStats() {
        return {
            ...this.stats,
            cacheValid: this.cache && (Date.now() - this.cacheTimestamp) < this.cacheTimeout,
            cachedIP: this.cache?.ip || null
        };
    }

    /**
     * Clear cache
     */
    clearCache() {
        this.cache = null;
        this.cacheTimestamp = 0;
        this.logger.debug('IPGeolocationService cache cleared');
    }

    /**
     * Check if service is supported
     */
    static isSupported() {
        return typeof fetch !== 'undefined';
    }

    /**
     * Get capabilities
     */
    static getCapabilities() {
        return {
            supported: IPGeolocationService.isSupported(),
            features: ['geolocation', 'timezone_mismatch', 'language_mismatch', 'vpn_detection']
        };
    }

    /**
     * Destroy service
     */
    destroy() {
        this.clearCache();
        this.logger.debug('IPGeolocationService destroyed');
    }
}

export default IPGeolocationService;
